import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { 
  getUsers, createUser, updateUser, deleteUser,
  getVisitPurposes, createVisitPurpose, deleteVisitPurpose,
  getCompanies, createCompany, updateCompany, deleteCompany,
  getStats
} from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogClose } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  Shield, Sun, Moon, LogOut, User, Users, Settings, Plus, 
  Trash2, Edit, Archive, LayoutDashboard, Key, CheckCircle2, Clock, Car, Building2, ClipboardList
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { useNavigate, Link } from 'react-router-dom';

export default function AdminPanel() {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [purposes, setPurposes] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('users');

  const loadData = useCallback(async () => {
    try {
      const [usersRes, purposesRes, companiesRes, statsRes] = await Promise.all([
        getUsers(),
        getVisitPurposes(),
        getCompanies(),
        getStats()
      ]);
      setUsers(usersRes.data);
      setPurposes(purposesRes.data);
      setCompanies(companiesRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Ошибка загрузки данных');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Загрузка...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 h-full w-64 border-r bg-card hidden lg:block">
        <div className="p-6">
          <div className="flex items-center gap-3 mb-8">
            <div className="p-2 rounded-md bg-primary">
              <Shield className="h-5 w-5 text-primary-foreground" />
            </div>
            <div>
              <h1 className="text-lg font-black tracking-tighter">SENTINEL</h1>
              <p className="text-xs text-muted-foreground">Администратор</p>
            </div>
          </div>

          <nav className="space-y-2">
            <Button
              variant={activeTab === 'users' ? 'secondary' : 'ghost'}
              className="w-full justify-start"
              onClick={() => setActiveTab('users')}
              data-testid="nav-users"
            >
              <Users className="h-4 w-4 mr-2" />
              Пользователи
            </Button>
            <Button
              variant={activeTab === 'companies' ? 'secondary' : 'ghost'}
              className="w-full justify-start"
              onClick={() => setActiveTab('companies')}
              data-testid="nav-companies"
            >
              <Building2 className="h-4 w-4 mr-2" />
              Компании
            </Button>
            <Button
              variant={activeTab === 'purposes' ? 'secondary' : 'ghost'}
              className="w-full justify-start"
              onClick={() => setActiveTab('purposes')}
              data-testid="nav-purposes"
            >
              <Settings className="h-4 w-4 mr-2" />
              Цели визита
            </Button>
            <Button
              variant={activeTab === 'stats' ? 'secondary' : 'ghost'}
              className="w-full justify-start"
              onClick={() => setActiveTab('stats')}
              data-testid="nav-stats"
            >
              <LayoutDashboard className="h-4 w-4 mr-2" />
              Статистика
            </Button>
          </nav>
        </div>

        <div className="absolute bottom-0 left-0 right-0 p-6 border-t">
          <Link to="/permanent-passes">
            <Button variant="outline" className="w-full mb-2 gap-2" data-testid="permanent-passes-link">
              <ClipboardList className="h-4 w-4" />
              Реестр пропусков
            </Button>
          </Link>
          <Link to="/archive">
            <Button variant="outline" className="w-full mb-4 gap-2" data-testid="archive-link">
              <Archive className="h-4 w-4" />
              Архив заявок
            </Button>
          </Link>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm">
              <User className="h-4 w-4" />
              <span className="truncate">{user?.full_name}</span>
            </div>
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="theme-toggle">
                {theme === 'light' ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
              </Button>
              <Button variant="ghost" size="icon" onClick={handleLogout} data-testid="logout-btn">
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="lg:hidden border-b bg-card sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-md bg-primary">
                <Shield className="h-5 w-5 text-primary-foreground" />
              </div>
              <h1 className="text-lg font-black tracking-tighter">SENTINEL</h1>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="icon" onClick={toggleTheme}>
                {theme === 'light' ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
              </Button>
              <Button variant="ghost" size="icon" onClick={handleLogout}>
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
          {/* Mobile Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-4">
            <TabsList className="w-full">
              <TabsTrigger value="users" className="flex-1">Пользователи</TabsTrigger>
              <TabsTrigger value="companies" className="flex-1">Компании</TabsTrigger>
              <TabsTrigger value="purposes" className="flex-1">Цели</TabsTrigger>
              <TabsTrigger value="stats" className="flex-1">Статистика</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </header>

      {/* Main Content */}
      <main className="lg:ml-64 min-h-screen">
        <div className="container mx-auto px-6 py-8 max-w-5xl">
          {activeTab === 'users' && (
            <UsersSection users={users} onRefresh={loadData} currentUserId={user?.id} />
          )}
          {activeTab === 'companies' && (
            <CompaniesSection companies={companies} onRefresh={loadData} />
          )}
          {activeTab === 'purposes' && (
            <PurposesSection purposes={purposes} onRefresh={loadData} />
          )}
          {activeTab === 'stats' && (
            <StatsSection stats={stats} users={users} />
          )}
        </div>
      </main>
    </div>
  );
}

function UsersSection({ users, onRefresh, currentUserId }) {
  const [isOpen, setIsOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [form, setForm] = useState({
    email: '',
    full_name: '',
    role: 'user',
    password: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const resetForm = () => {
    setForm({ email: '', full_name: '', role: 'user', password: '' });
    setEditUser(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (editUser) {
        const updateData = { ...form };
        if (!updateData.password) delete updateData.password;
        await updateUser(editUser.id, updateData);
        toast.success('Пользователь обновлён');
      } else {
        await createUser(form);
        toast.success('Пользователь создан');
      }
      resetForm();
      setIsOpen(false);
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ошибка');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (user) => {
    setEditUser(user);
    setForm({
      email: user.email,
      full_name: user.full_name,
      role: user.role,
      password: ''
    });
    setIsOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Удалить пользователя?')) return;
    try {
      await deleteUser(id);
      toast.success('Пользователь удалён');
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ошибка');
    }
  };

  const getRoleBadge = (role) => {
    switch (role) {
      case 'admin':
        return <Badge className="bg-primary">Админ</Badge>;
      case 'security':
        return <Badge className="bg-emerald-600">Охрана</Badge>;
      case 'orp':
        return <Badge className="bg-violet-600">Сотрудник ОРП</Badge>;
      default:
        return <Badge variant="secondary">Заявитель</Badge>;
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Пользователи</h2>
          <p className="text-muted-foreground">Управление учётными записями</p>
        </div>
        <Dialog open={isOpen} onOpenChange={(open) => { setIsOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="gap-2" data-testid="add-user-btn">
              <Plus className="h-4 w-4" />
              Добавить
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editUser ? 'Редактировать пользователя' : 'Новый пользователь'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({...form, email: e.target.value})}
                  required
                  data-testid="user-email"
                />
              </div>
              <div className="space-y-2">
                <Label>ФИО</Label>
                <Input
                  value={form.full_name}
                  onChange={(e) => setForm({...form, full_name: e.target.value})}
                  required
                  data-testid="user-fullname"
                />
              </div>
              <div className="space-y-2">
                <Label>Роль</Label>
                <Select value={form.role} onValueChange={(value) => setForm({...form, role: value})}>
                  <SelectTrigger data-testid="user-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="user">Заявитель</SelectItem>
                    <SelectItem value="orp">Сотрудник ОРП</SelectItem>
                    <SelectItem value="security">Охрана</SelectItem>
                    <SelectItem value="admin">Администратор</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{editUser ? 'Новый пароль (оставьте пустым)' : 'Пароль'}</Label>
                <Input
                  type="password"
                  value={form.password}
                  onChange={(e) => setForm({...form, password: e.target.value})}
                  required={!editUser}
                  data-testid="user-password"
                />
              </div>
              <DialogFooter>
                <DialogClose asChild>
                  <Button type="button" variant="outline">Отмена</Button>
                </DialogClose>
                <Button type="submit" disabled={submitting} data-testid="save-user-btn">
                  {submitting ? 'Сохранение...' : 'Сохранить'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Пользователь</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Роль</TableHead>
                <TableHead>Создан</TableHead>
                <TableHead className="text-right">Действия</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map(u => (
                <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                  <TableCell className="font-medium">{u.full_name}</TableCell>
                  <TableCell>{u.email}</TableCell>
                  <TableCell>{getRoleBadge(u.role)}</TableCell>
                  <TableCell className="text-muted-foreground">
                    {format(new Date(u.created_at), 'dd.MM.yyyy')}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(u)}
                        data-testid={`edit-user-${u.id}`}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      {u.id !== currentUserId && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive hover:text-destructive"
                          onClick={() => handleDelete(u.id)}
                          data-testid={`delete-user-${u.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

function PurposesSection({ purposes, onRefresh }) {
  const [isOpen, setIsOpen] = useState(false);
  const [form, setForm] = useState({ name: '', type: 'person' });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await createVisitPurpose(form);
      toast.success('Цель визита добавлена');
      setForm({ name: '', type: 'person' });
      setIsOpen(false);
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ошибка');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Удалить цель визита?')) return;
    try {
      await deleteVisitPurpose(id);
      toast.success('Цель визита удалена');
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ошибка');
    }
  };

  const personPurposes = purposes.filter(p => p.type === 'person');
  const vehiclePurposes = purposes.filter(p => p.type === 'vehicle');

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Цели визита</h2>
          <p className="text-muted-foreground">Настройка справочника целей</p>
        </div>
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
          <DialogTrigger asChild>
            <Button className="gap-2" data-testid="add-purpose-btn">
              <Plus className="h-4 w-4" />
              Добавить
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Новая цель визита</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Название</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({...form, name: e.target.value})}
                  placeholder="Например: Собеседование"
                  required
                  data-testid="purpose-name"
                />
              </div>
              <div className="space-y-2">
                <Label>Тип</Label>
                <Select value={form.type} onValueChange={(value) => setForm({...form, type: value})}>
                  <SelectTrigger data-testid="purpose-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="person">Для посетителей</SelectItem>
                    <SelectItem value="vehicle">Для автомобилей</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <DialogFooter>
                <DialogClose asChild>
                  <Button type="button" variant="outline">Отмена</Button>
                </DialogClose>
                <Button type="submit" disabled={submitting} data-testid="save-purpose-btn">
                  {submitting ? 'Сохранение...' : 'Сохранить'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <User className="h-4 w-4" />
              Цели для посетителей
            </CardTitle>
          </CardHeader>
          <CardContent>
            {personPurposes.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">Нет целей визита</p>
            ) : (
              <div className="space-y-2">
                {personPurposes.map(p => (
                  <div 
                    key={p.id} 
                    className="flex items-center justify-between p-3 rounded-md bg-muted/50"
                    data-testid={`purpose-item-${p.id}`}
                  >
                    <span>{p.name}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive hover:text-destructive"
                      onClick={() => handleDelete(p.id)}
                      data-testid={`delete-purpose-${p.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Car className="h-4 w-4" />
              Цели для автомобилей
            </CardTitle>
          </CardHeader>
          <CardContent>
            {vehiclePurposes.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">Нет целей визита</p>
            ) : (
              <div className="space-y-2">
                {vehiclePurposes.map(p => (
                  <div 
                    key={p.id} 
                    className="flex items-center justify-between p-3 rounded-md bg-muted/50"
                    data-testid={`purpose-item-${p.id}`}
                  >
                    <span>{p.name}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive hover:text-destructive"
                      onClick={() => handleDelete(p.id)}
                      data-testid={`delete-purpose-${p.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function StatsSection({ stats, users }) {
  const adminCount = users.filter(u => u.role === 'admin').length;
  const securityCount = users.filter(u => u.role === 'security').length;
  const orpCount = users.filter(u => u.role === 'orp').length;
  const userCount = users.filter(u => u.role === 'user').length;

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h2 className="text-2xl font-bold">Статистика</h2>
        <p className="text-muted-foreground">Обзор системы</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Активные заявки</p>
                <p className="text-3xl font-black">{stats.total_active || 0}</p>
              </div>
              <CheckCircle2 className="h-8 w-8 text-emerald-500/50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Ожидается сегодня</p>
                <p className="text-3xl font-black text-amber-500">{stats.today_expected || 0}</p>
              </div>
              <Clock className="h-8 w-8 text-amber-500/50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">На территории</p>
                <p className="text-3xl font-black text-blue-500">{stats.arrived || 0}</p>
              </div>
              <Users className="h-8 w-8 text-blue-500/50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Пользователей</p>
                <p className="text-3xl font-black">{users.length}</p>
              </div>
              <Key className="h-8 w-8 text-muted-foreground/50" />
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Распределение по ролям</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-primary" />
                <span>Администраторы</span>
              </div>
              <span className="font-semibold">{adminCount}</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-emerald-600" />
                <span>Охрана</span>
              </div>
              <span className="font-semibold">{securityCount}</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-violet-600" />
                <span>Сотрудники ОРП</span>
              </div>
              <span className="font-semibold">{orpCount}</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-muted-foreground" />
                <span>Заявители</span>
              </div>
              <span className="font-semibold">{userCount}</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Companies Section
function CompaniesSection({ companies, onRefresh }) {
  const [isOpen, setIsOpen] = useState(false);
  const [editCompany, setEditCompany] = useState(null);
  const [form, setForm] = useState({ name: '', description: '' });
  const [submitting, setSubmitting] = useState(false);

  const resetForm = () => {
    setForm({ name: '', description: '' });
    setEditCompany(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (editCompany) {
        await updateCompany(editCompany.id, form);
        toast.success('Компания обновлена');
      } else {
        await createCompany(form);
        toast.success('Компания добавлена');
      }
      resetForm();
      setIsOpen(false);
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ошибка');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (company) => {
    setEditCompany(company);
    setForm({ name: company.name, description: company.description || '' });
    setIsOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Удалить компанию?')) return;
    try {
      await deleteCompany(id);
      toast.success('Компания удалена');
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ошибка');
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Компании</h2>
          <p className="text-muted-foreground">Справочник организаций для постоянных пропусков</p>
        </div>
        <Dialog open={isOpen} onOpenChange={(open) => { setIsOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="gap-2" data-testid="add-company-btn">
              <Plus className="h-4 w-4" />
              Добавить
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editCompany ? 'Редактировать компанию' : 'Новая компания'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Название</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({...form, name: e.target.value})}
                  placeholder="ООО Рога и Копыта"
                  required
                  data-testid="company-name"
                />
              </div>
              <div className="space-y-2">
                <Label>Описание</Label>
                <Textarea
                  value={form.description}
                  onChange={(e) => setForm({...form, description: e.target.value})}
                  placeholder="Дополнительная информация..."
                  rows={2}
                  data-testid="company-description"
                />
              </div>
              <DialogFooter>
                <DialogClose asChild>
                  <Button type="button" variant="outline">Отмена</Button>
                </DialogClose>
                <Button type="submit" disabled={submitting} data-testid="save-company-btn">
                  {submitting ? 'Сохранение...' : 'Сохранить'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardContent className="p-0">
          {companies.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              Нет компаний
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Название</TableHead>
                  <TableHead>Описание</TableHead>
                  <TableHead>Создана</TableHead>
                  <TableHead className="text-right">Действия</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {companies.map(c => (
                  <TableRow key={c.id} data-testid={`company-row-${c.id}`}>
                    <TableCell className="font-medium">{c.name}</TableCell>
                    <TableCell className="text-muted-foreground">{c.description || '—'}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {format(new Date(c.created_at), 'dd.MM.yyyy')}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(c)}
                          data-testid={`edit-company-${c.id}`}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive hover:text-destructive"
                          onClick={() => handleDelete(c.id)}
                          data-testid={`delete-company-${c.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
